import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subcontractAPI } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  GitFork, ArrowUpRight, ArrowDownLeft, Clock, AlertTriangle, FileCheck,
  TrendingUp, Package, Truck, Timer, BarChart3, RefreshCw
} from 'lucide-react';

export default function SubcontractDashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadKPIs = async () => {
    setLoading(true);
    try {
      const res = await subcontractAPI.getDashboardKPIs();
      setKpis(res.data);
    } catch { toast.error('Failed to load dashboard'); }
    setLoading(false);
  };

  useEffect(() => { loadKPIs(); }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading dashboard...</div>;
  if (!kpis) return <div className="text-center py-12 text-muted-foreground text-sm">No data available</div>;

  const formatCurrency = (v) => (v || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

  return (
    <div className="space-y-6" data-testid="subcontract-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Subcontracting Dashboard</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Overview of all outsourcing, job work, and calibration activities</p>
        </div>
        <Button size="sm" variant="outline" onClick={loadKPIs} className="h-8 text-xs" data-testid="refresh-dashboard">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="p-3 border-l-4 border-l-blue-500" data-testid="kpi-total-outbound">
          <div className="flex items-center gap-2 mb-1.5">
            <ArrowUpRight className="w-4 h-4 text-blue-500" />
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Outbound</span>
          </div>
          <p className="text-2xl font-bold">{kpis.total_outbound}</p>
          <p className="text-[10px] text-muted-foreground">{kpis.active_outbound} active</p>
        </Card>

        <Card className="p-3 border-l-4 border-l-teal-500" data-testid="kpi-total-inbound">
          <div className="flex items-center gap-2 mb-1.5">
            <ArrowDownLeft className="w-4 h-4 text-teal-500" />
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Inbound</span>
          </div>
          <p className="text-2xl font-bold">{kpis.total_inbound}</p>
          <p className="text-[10px] text-muted-foreground">{kpis.active_inbound} active</p>
        </Card>

        <Card className="p-3 border-l-4 border-l-amber-500" data-testid="kpi-pending-receipts">
          <div className="flex items-center gap-2 mb-1.5">
            <Package className="w-4 h-4 text-amber-500" />
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Pending Receipt</span>
          </div>
          <p className="text-2xl font-bold">{kpis.pending_receipts}</p>
          <p className="text-[10px] text-muted-foreground">Sent, awaiting return</p>
        </Card>

        <Card className="p-3 border-l-4 border-l-red-500" data-testid="kpi-overdue">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Overdue</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{kpis.overdue}</p>
          <p className="text-[10px] text-muted-foreground">Past due date</p>
        </Card>

        <Card className="p-3 border-l-4 border-l-violet-500" data-testid="kpi-invoice-backlog">
          <div className="flex items-center gap-2 mb-1.5">
            <FileCheck className="w-4 h-4 text-violet-500" />
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Invoice Backlog</span>
          </div>
          <p className="text-2xl font-bold">{kpis.invoice_backlog}</p>
          <p className="text-[10px] text-muted-foreground">All received, no invoice</p>
        </Card>

        <Card className="p-3 border-l-4 border-l-green-500" data-testid="kpi-closed">
          <div className="flex items-center gap-2 mb-1.5">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Closed</span>
          </div>
          <p className="text-2xl font-bold">{kpis.closed_challans}</p>
          <p className="text-[10px] text-muted-foreground">Completed & invoiced</p>
        </Card>
      </div>

      {/* Inbound Challans Ready for Closure */}
      <ReadyForClosureCard kpis={kpis} onReload={loadKPIs} />

      {/* Challan Aging & Invoice Backlog */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Challan Aging */}
        <Card className="p-4" data-testid="challan-aging-card">
          <div className="flex items-center gap-2 mb-3">
            <Timer className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold">Challan Aging (Outbound)</h3>
          </div>
          {kpis.aging_buckets?.length > 0 ? (
            <div className="space-y-2">
              {kpis.aging_buckets.map((b, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{b.label}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${b.color}`} style={{ width: `${Math.min(100, (b.count / Math.max(1, kpis.active_outbound)) * 100)}%` }} />
                    </div>
                    <span className="text-xs font-medium w-6 text-right">{b.count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-4 text-center">No active outbound challans</p>
          )}
        </Card>

        {/* Supplier Performance */}
        <Card className="p-4" data-testid="supplier-perf-card">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold">Supplier Performance</h3>
          </div>
          {kpis.supplier_stats?.length > 0 ? (
            <div className="space-y-2.5">
              {kpis.supplier_stats.slice(0, 8).map((s, idx) => (
                <div key={idx} className="flex items-center justify-between py-1 border-b border-muted/50 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{s.supplier_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className="text-[8px] px-1 py-0">{s.total_challans} challans</Badge>
                      {s.pending > 0 && <Badge variant="outline" className="text-[8px] px-1 py-0 text-amber-700 bg-amber-50 border-amber-200">{s.pending} pending</Badge>}
                      {s.overdue > 0 && <Badge variant="destructive" className="text-[8px] px-1 py-0">{s.overdue} overdue</Badge>}
                    </div>
                  </div>
                  <div className="text-right ml-3 flex-shrink-0">
                    <p className="text-[10px] text-muted-foreground">Avg Days</p>
                    <p className="text-xs font-semibold">{s.avg_days || '-'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-4 text-center">No supplier data</p>
          )}
        </Card>
      </div>

      {/* Invoice Summary & Recent Activity */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Invoice Summary */}
        <Card className="p-4" data-testid="invoice-summary-card">
          <div className="flex items-center gap-2 mb-3">
            <FileCheck className="w-4 h-4 text-violet-600" />
            <h3 className="text-sm font-semibold">Invoice Summary</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-2.5 bg-green-50 rounded-md border border-green-200">
              <p className="text-[10px] text-green-700 font-medium">Invoiced Amount</p>
              <p className="text-sm font-bold text-green-800">{formatCurrency(kpis.total_invoiced_amount)}</p>
              <p className="text-[10px] text-green-600">{kpis.total_service_entries} entries</p>
            </div>
            <div className="p-2.5 bg-amber-50 rounded-md border border-amber-200">
              <p className="text-[10px] text-amber-700 font-medium">Pending Invoice</p>
              <p className="text-sm font-bold text-amber-800">{kpis.invoice_backlog} challans</p>
              <p className="text-[10px] text-amber-600">All received, no invoice</p>
            </div>
          </div>
        </Card>

        {/* Recent Challans */}
        <Card className="p-4" data-testid="recent-challans-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-600" />
              <h3 className="text-sm font-semibold">Recent Activity</h3>
            </div>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => navigate('/subcontract/challans')} data-testid="view-all-challans">
              View All
            </Button>
          </div>
          <div className="space-y-2">
            {(kpis.recent_challans || []).slice(0, 5).map(c => (
              <div key={c.id} className="flex items-center justify-between py-1.5 border-b border-muted/50 last:border-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {c.order_type === 'outbound' ? <ArrowUpRight className="w-3 h-3 text-blue-500 flex-shrink-0" /> : <ArrowDownLeft className="w-3 h-3 text-teal-500 flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium truncate">{c.challan_number}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{c.supplier_name}</p>
                  </div>
                </div>
                <Badge variant="outline" className={`text-[8px] px-1.5 py-0 ml-2 flex-shrink-0 ${
                  c.status === 'closed' ? 'bg-green-50 text-green-700 border-green-200' :
                  c.status === 'all_received' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                  c.status === 'sent' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  c.status === 'partially_received' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-slate-50 text-slate-600 border-slate-200'
                }`}>{c.status?.replace(/_/g, ' ')}</Badge>
              </div>
            ))}
            {(!kpis.recent_challans || kpis.recent_challans.length === 0) && (
              <p className="text-xs text-muted-foreground py-4 text-center">No recent challans</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}


// ============ Inbound Ready-for-Closure Card ============

function ReadyForClosureCard({ kpis, onReload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closingId, setClosingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await subcontractAPI.listInboundReadyForClosure();
      setRows(res.data?.challans || []);
    } catch { toast.error('Failed to load ready-for-closure'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const closeOne = async (c) => {
    if (!window.confirm(`Close inbound challan ${c.challan_number}? This marks the job as complete.`)) return;
    setClosingId(c.id);
    try {
      await subcontractAPI.closeInboundChallan(c.id, { remarks: 'Closed from dashboard - ready-for-closure tool' });
      toast.success(`${c.challan_number} closed`);
      await load();
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Close failed');
    } finally {
      setClosingId(null);
    }
  };

  return (
    <Card className="p-0 overflow-hidden" data-testid="ready-for-closure-card">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-emerald-50 to-slate-50">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <FileCheck className="w-4 h-4 text-emerald-600" />
            Inbound Challans Available for Closure
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Received jobs with all items dispatched. {kpis.inbound_auto_close_eligible > 0 ? (
              <span className="text-emerald-700 font-medium">{kpis.inbound_auto_close_eligible} auto-close eligible</span>
            ) : 'Auto-close runs daily at 09:00 UTC for challans idle ≥7 days.'}
          </p>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={load} disabled={loading}>
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="p-4 text-center text-xs text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-center text-xs text-muted-foreground">No inbound challans are currently ready for closure.</div>
      ) : (
        <div className="overflow-auto max-h-96">
          <table className="w-full text-xs" data-testid="ready-for-closure-table">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Challan</th>
                <th className="text-left px-3 py-2 font-medium">Customer</th>
                <th className="text-left px-3 py-2 font-medium">Cust DC# / PO#</th>
                <th className="text-right px-3 py-2 font-medium">Coverage</th>
                <th className="text-right px-3 py-2 font-medium">Days Idle</th>
                <th className="text-left px-3 py-2 font-medium">Last Dispatch</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className={`border-t hover:bg-slate-50/50 ${r.auto_close_eligible ? 'bg-emerald-50/40' : ''}`}>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{r.challan_number}</td>
                  <td className="px-3 py-1.5 truncate max-w-[180px]">{r.supplier_name}</td>
                  <td className="px-3 py-1.5 text-[11px] text-muted-foreground">
                    {r.customer_challan_number || '—'}{r.purchase_order_number ? ` / ${r.purchase_order_number}` : ''}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    <span className={r.all_items_dispatched ? 'text-emerald-700 font-semibold' : ''}>{r.coverage_pct}%</span>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.days_since_last_dispatch}</td>
                  <td className="px-3 py-1.5 text-[11px]">{r.last_dispatch_date ? new Date(r.last_dispatch_date).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-1.5">
                    {r.auto_close_eligible
                      ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px] hover:bg-emerald-100">Auto-close ready</Badge>
                      : r.all_items_dispatched
                        ? <Badge variant="outline" className="text-[10px]">Ready</Badge>
                        : <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Partial ({r.coverage_pct}%)</Badge>}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <Button size="sm" variant="outline" className="h-7 text-[10px]"
                      disabled={closingId === r.id}
                      onClick={() => closeOne(r)}
                      data-testid={`close-inbound-${r.challan_number}`}>
                      {closingId === r.id ? 'Closing…' : 'Close Now'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
